import { useState } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Box,
  ProgressBar,
  Divider,
  Icon,
  Select,
  TextField,
  Button,
} from '@shopify/polaris';
import { CheckCircleIcon, AlertCircleIcon, XIcon } from '@shopify/polaris-icons';
import type { CategoryTemplate, CategoryConfidence, TemplateAttribute } from '../types';
import { useSetProductAttribute } from '../hooks/useCategory';

/**
 * T077 [US5] CategoryTemplateView — redesigned category & attribute section.
 * Shows detected category, confidence, attribute completeness progress,
 * and per-attribute status in a clean compact layout.
 * Missing attributes have an inline fill-in TextField so users can add
 * values without leaving the page.
 */

interface CategoryTemplateViewProps {
  readonly template: CategoryTemplate;
  readonly presentAttributes: string[];
  readonly attributeValues?: Record<string, string>;
  readonly attributeValueSources?: Record<string, string>;
  readonly confidence: CategoryConfidence | null;
  readonly allTemplates: CategoryTemplate[];
  readonly categoryOverride: string | null;
  readonly detectedCategory: string | null;
  readonly onOverride: (slug: string) => void;
  readonly productId?: string;
}

function confidenceTone(c: CategoryConfidence): 'success' | 'warning' | 'attention' {
  if (c === 'HIGH') return 'success';
  if (c === 'MEDIUM') return 'warning';
  return 'attention';
}

function AttributeRow({
  attr,
  present,
  tone,
  productId,
  value,
  source,
}: Readonly<{
  attr: TemplateAttribute;
  present: boolean;
  tone: 'critical' | 'warning';
  productId?: string;
  value?: string;
  source?: string;
}>) {
  const [expanded, setExpanded] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const setAttr = useSetProductAttribute(productId ?? '');

  const isUserFill = source === 'user_fill';
  const canEdit = present && isUserFill && productId;

  const handleEdit = () => {
    setInputValue(value ?? '');
    setExpanded(true);
  };

  const handleSave = () => {
    if (!inputValue.trim() || !productId) return;
    setAttr.mutate(
      { key: attr.key, value: inputValue.trim(), label: attr.label },
      {
        onSuccess: () => {
          setExpanded(false);
          setInputValue('');
        },
      },
    );
  };

  return (
    <Box paddingBlockStart="050" paddingBlockEnd="050">
      <InlineStack align="space-between" blockAlign="start" wrap={false}>
        <InlineStack gap="200" blockAlign="start" wrap={false}>
          <Box minWidth="20px">
            <Icon
              source={present ? CheckCircleIcon : AlertCircleIcon}
              tone={present ? 'success' : tone}
            />
          </Box>
          <BlockStack gap="0">
            <Text as="span" variant="bodySm" fontWeight={present ? 'regular' : 'medium'}>
              {attr.label}
            </Text>
            {!present && (
              <Text as="span" variant="bodyXs" tone="subdued">
                <em style={{ fontStyle: 'italic', opacity: 0.6, fontSize: '10px' }}>e.g. {attr.example}</em>
              </Text>
            )}
            {present && value && (
              <Text as="span" variant="bodyXs" tone="subdued">
                {value}
              </Text>
            )}
          </BlockStack>
        </InlineStack>
        <InlineStack gap="200" blockAlign="center">
          {present ? (
            <>
              <Badge tone="success">Found</Badge>
              {canEdit && !expanded && (
                <Button variant="plain" size="slim" onClick={handleEdit}>
                  Edit
                </Button>
              )}
            </>
          ) : (
            <>
              <Badge tone={tone}>Missing</Badge>
              {productId && !expanded && (
                <Button variant="plain" size="slim" onClick={() => setExpanded(true)}>
                  Fill in
                </Button>
              )}
            </>
          )}
        </InlineStack>
      </InlineStack>

      {((!present && expanded) || (present && expanded && canEdit)) && productId && (
        <Box paddingBlockStart="200" paddingInlineStart="600">
          <InlineStack gap="200" blockAlign="center">
            <div style={{ flex: 1 }}>
              <TextField
                label={attr.label}
                labelHidden
                value={inputValue}
                onChange={setInputValue}
                placeholder=""
                autoComplete="off"
                disabled={setAttr.isPending}
              />
            </div>
            <Button
              onClick={handleSave}
              disabled={!inputValue.trim() || setAttr.isPending}
              variant="primary"
              size="slim"
              loading={setAttr.isPending}
            >
              Save
            </Button>
            <Button
              variant="plain"
              size="slim"
              icon={XIcon}
              accessibilityLabel="Cancel"
              onClick={() => {
                setExpanded(false);
                setInputValue('');
              }}
              disabled={setAttr.isPending}
            />
          </InlineStack>
        </Box>
      )}
    </Box>
  );
}

export function CategoryTemplateView({
  template,
  presentAttributes,
  attributeValues,
  attributeValueSources,
  confidence,
  allTemplates,
  categoryOverride,
  detectedCategory: _detectedCategory,
  onOverride,
  productId,
}: CategoryTemplateViewProps) {
  const presentSet = new Set(presentAttributes.map((k) => k.toLowerCase()));

  const reqAttrs = template.requiredAttributes;
  const recAttrs = template.recommendedAttrs;
  const totalAttrs = reqAttrs.length + recAttrs.length;
  const presentCount = [...reqAttrs, ...recAttrs].filter((a) =>
    presentSet.has(a.key.toLowerCase()),
  ).length;
  const completionPct = totalAttrs > 0 ? Math.round((presentCount / totalAttrs) * 100) : 0;

  const missingReqCount = reqAttrs.filter((a) => !presentSet.has(a.key.toLowerCase())).length;

  let progressTone: 'success' | 'highlight' | 'critical' = 'critical';
  if (completionPct >= 80) progressTone = 'success';
  else if (completionPct >= 40) progressTone = 'highlight';

  const overrideOptions = [
    { label: 'Auto-detected', value: '' },
    ...allTemplates.map((t) => ({ label: t.displayName, value: t.slug })),
  ];

  return (
    <Card>
      <BlockStack gap="400">
        {/* ── Header ── */}
        <InlineStack align="space-between" blockAlign="center" wrap={false}>
          <InlineStack gap="200" blockAlign="center">
            <Text as="h2" variant="headingMd">
              {template.displayName}
            </Text>
            {confidence && (
              <Badge tone={confidenceTone(confidence)}>
                {confidence}
              </Badge>
            )}
          </InlineStack>
          <Text as="span" variant="bodySm" tone="subdued">
            {presentCount}/{totalAttrs} attributes
          </Text>
        </InlineStack>

        {/* ── Completeness Bar ── */}
        <BlockStack gap="100">
          <ProgressBar progress={completionPct} size="small" tone={progressTone} />
          <InlineStack align="space-between">
            <Text as="span" variant="bodySm" tone="subdued">
              Attribute completeness
            </Text>
            <Text
              as="span"
              variant="bodySm"
              fontWeight="semibold"
              tone={progressTone === 'critical' ? 'critical' : undefined}
            >
              {completionPct}%
            </Text>
          </InlineStack>
        </BlockStack>

        <Divider />

        {/* ── Required Attributes ── */}
        <BlockStack gap="200">
          <InlineStack align="space-between" blockAlign="center">
            <Text as="h3" variant="headingSm">
              Required
            </Text>
            {missingReqCount > 0 && (
              <Badge tone="critical">
                {`${missingReqCount} missing`}
              </Badge>
            )}
          </InlineStack>
          <BlockStack gap="0">
            {reqAttrs.map((attr) => (
              <AttributeRow
                key={attr.key}
                attr={attr}
                present={presentSet.has(attr.key.toLowerCase())}
                tone="critical"
                productId={productId}
                value={attributeValues?.[attr.key.toLowerCase()]}
                source={attributeValueSources?.[attr.key.toLowerCase()]}
              />
            ))}
          </BlockStack>
        </BlockStack>

        <Divider />

        {/* ── Recommended Attributes ── */}
        <BlockStack gap="200">
          <Text as="h3" variant="headingSm">
            Recommended
          </Text>
          <BlockStack gap="0">
            {recAttrs.map((attr) => (
              <AttributeRow
                key={attr.key}
                attr={attr}
                present={presentSet.has(attr.key.toLowerCase())}
                tone="warning"
                productId={productId}
                value={attributeValues?.[attr.key.toLowerCase()]}
                source={attributeValueSources?.[attr.key.toLowerCase()]}
              />
            ))}
          </BlockStack>
        </BlockStack>

        <Divider />

        {/* ── Category Override ── */}
        <Select
          label="Override category"
          options={overrideOptions}
          value={categoryOverride ?? ''}
          onChange={(value) => {
            if (value) onOverride(value);
          }}
        />
      </BlockStack>
    </Card>
  );
}
